/**
 * 🆕 [Phase 81 Popover — 2026-06-22]
 * Composant réutilisable pour expliquer aux employés pourquoi les heures
 * budgétées ne sont pas encore visibles.
 *
 * Usage :
 *   <HeuresEnAttentePopover />
 *
 * Affiche une zone cliquable "🔒 Heures en attente d'approbation"
 * qui, au clic, ouvre un popover Shadcn expliquant le workflow d'approbation.
 *
 * Cohérent avec les défenses en profondeur Phase 81 :
 *   - ChantierDuJour.jsx (BT internes)
 *   - tout futur consommateur de `heures_masquees_pour_role`
 */
import React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Lock, Info } from 'lucide-react';

export default function HeuresEnAttentePopover({
  // Permet de personnaliser le wrapper externe (ex. icône à gauche, classes)
  className = '',
  // Permet d'utiliser un trigger personnalisé (sinon : texte par défaut)
  triggerLabel = "Heures en attente d'approbation",
  // taille du label
  size = 'sm',
}) {
  const textSize = size === 'xs' ? 'text-xs' : 'text-sm';
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1 ${textSize} italic text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors cursor-help underline decoration-dotted underline-offset-2 ${className}`}
          aria-label="Pourquoi je ne vois pas ce chiffre ?"
          data-testid="heures-en-attente-popover-trigger"
        >
          <Lock className="w-3 h-3 shrink-0" aria-hidden="true" />
          <span>{triggerLabel}</span>
          <Info className="w-3 h-3 shrink-0 opacity-60" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="center"
        sideOffset={6}
        className="w-72 max-w-[90vw] p-4 text-sm"
        data-testid="heures-en-attente-popover-content"
      >
        <div className="flex items-start gap-2 mb-2">
          <Lock className="w-4 h-4 mt-0.5 text-amber-600 shrink-0" />
          <h4 className="font-semibold text-slate-900 dark:text-slate-100">
            Heures en attente d'approbation
          </h4>
        </div>
        <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
          Les propriétaires doivent d'abord approuver le nombre d'heures
          budgétées avant qu'il soit visible. Cette règle évite les
          malentendus si le nombre proposé par le système diffère du
          nombre finalement approuvé.
        </p>
        <p className="mt-2 text-slate-600 dark:text-slate-400 text-xs">
          Tu verras le chiffre dès qu'il sera approuvé.
        </p>
      </PopoverContent>
    </Popover>
  );
}
